import { ProblemKind, SentryReportTags } from './problem-kind';

/**
 * How the two reports the fatal error screen sends are named, tagged and tiered
 * (df-5a40caa42bda).
 *
 * A silent report's NAME is its Sentry message, and the message is Sentry's grouping key.
 * Both reports used to carry a bare name (`FatalError`, `FatalErrorPage`), so every cause
 * of the screen — a TypeError during boot, a CouchDB 403, Lockdown Mode — landed in the
 * same Sentry issue, and a new cause never opened an issue of its own. The name now ends
 * with the screen type and the error's class name, so each cause groups separately. See
 * design-docs/telemetry.md, "Fatal error screen reports".
 *
 * No Angular and no Sentry import, so it stays unit-testable.
 */

/** What a report needs from the screen's params. Looser than `FatalErrorPageParams` on
 * purpose: the screen reads them through `getMyApp()`, whose interface types `type` as a
 * plain string. */
export type FatalErrorReportParams = { type: string; error?: unknown } | undefined;

/** The two reports that name a fatal error screen. */
export enum FatalErrorReport {
  /** Sent by `LoggingService.fatalError` as it routes to the screen. */
  Routed = 'FatalError',
  /** Sent by the screen itself each time it is shown. */
  Shown = 'FatalErrorPage',
}

/** Sentry tag carrying the screen type. */
export const FATAL_TYPE_TAG = 'fatal_type';
/** Sentry tag carrying the error's class name, or the word for its shape. */
export const FATAL_ERROR_NAME_TAG = 'fatal_error_name';

/** The longest error class name kept; anything past it is cut off. */
const MAX_ERROR_NAME_LENGTH = 40;

export interface FatalErrorReportShape {
  /** The report name, which becomes the Sentry message after the tier prefix. */
  name: string;
  /** Which silent tier the report goes out at. */
  kind: ProblemKind.Info | ProblemKind.Problem;
  tags: SentryReportTags;
}

/**
 * A low-cardinality word for the error a fatal screen carries.
 *
 * Only the error's class name is used, never its message: the message holds ids, URLs and
 * numbers, and putting it in the grouping key would open a new Sentry issue per user.
 * The callers pass an Error, a PouchDB error object (whose `name` is a word like
 * `forbidden`), a string, a plain object of details, or nothing.
 */
export function fatalErrorName(error: unknown): string {
  // if there is no error at all, say so
  if (error === undefined || error === null) {
    return 'no_error';
  }
  // if it is a bare string, its text is a message and must not become part of the name
  if (typeof error === 'string') {
    return 'text';
  }
  // if it carries a class name, keep only the safe characters of it
  const rawName = typeof error === 'object' ? (error as { name?: unknown }).name : undefined;
  if (typeof rawName === 'string') {
    const cleaned = rawName.replace(/[^A-Za-z0-9_]/g, '').slice(0, MAX_ERROR_NAME_LENGTH);
    if (cleaned) {
      return cleaned;
    }
  }
  // otherwise it is a bundle of details with no name
  return 'details';
}

/**
 * Work out the name, tier and tags of one fatal error screen report.
 *
 * Lockdown Mode goes out at the informational tier: it is a setting on the user's own
 * device that the app cannot change, so nobody can act on it. Every other cause stays a
 * problem.
 */
export function describeFatalErrorReport(
  report: FatalErrorReport,
  params: FatalErrorReportParams,
): FatalErrorReportShape {
  const type = params?.type ?? 'unknown';
  const errorName = fatalErrorName(params?.error);
  const kind = type === 'lockdown_mode_error' ? ProblemKind.Info : ProblemKind.Problem;
  return {
    name: `${report} ${type} ${errorName}`,
    kind,
    tags: { [FATAL_TYPE_TAG]: type, [FATAL_ERROR_NAME_TAG]: errorName },
  };
}
